import React, { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MeetingsDrawer from "./MeetingsDrawer";
import {
  getAllMeetingsApi,
  createMursalMeetingApi,
  createExchangeMeetingApi,
  getMeetingJoinTokenApi,
} from "../../../../api/api";
import { getAuthUser, getUserEmail } from "../../../../utils/auth";
import "./Meetings.scss";

// ==================== DATE HELPERS ====================
function toDateObj(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function addMonths(date, months) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  return addDays(d, -day);
}

function formatLong(date) {
  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatShort(date) {
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function toMinutes(time) {
  if (!time) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function formatTimeRange(start, end) {
  return `${start || ""} - ${end || ""}`;
}

// Parses "YYYY-MM-DDTHH:MM:SS..." by splitting the string directly —
// never via `new Date(str)` — so the numbers we use are always exactly
// what came back from the API, with zero timezone reinterpretation.
function parseAsIs(dateStr) {
  if (!dateStr) return null;
  const [datePart, timePart = "00:00"] = dateStr.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);
  return { year, month, day, hour, minute };
}

// Builds a Date from those literal numbers (local constructor — no
// timezone math), purely so status comparisons (now vs start/end) work
// using the same wall-clock numbers the API gave us.
function toLocalDate(dateStr) {
  const p = parseAsIs(dateStr);
  if (!p) return null;
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, 0);
}

// ==================== CONSTANTS ====================
const TODAY_STR = toDateStr(new Date());
const VIEWS = ["Day", "Week", "Month", "Schedule"];

// ==================== STATUS HELPERS ====================
function getMeetingStatus(meeting) {
  if (!meeting) return "upcoming";
  if (meeting.meetingStatus === "CANCELLED") return "completed";
  if (meeting.meetingStatus === "COMPLETED") return "completed";
  if (meeting.type === "exchange" && !meeting.mursalLivekitEnabled)
    return "outlook";

  const now = new Date();
  const startTime = toLocalDate(meeting.startTimeIso || meeting.startTime);
  const endTime = toLocalDate(meeting.endTimeIso || meeting.endTime);

  // If meeting is in the past (ended), show as completed
  if (now > endTime) return "completed";

  // If meeting is currently active (between start and end)
  if (now >= startTime && now <= endTime && meeting.mursalLivekitEnabled)
    return "live";

  // If meeting is in the future
  if (now < startTime) return "upcoming";

  return "upcoming";
}

const STATUS_LABEL = {
  live: "Join Now",
  upcoming: "Upcoming",
  completed: "Completed",
  outlook: "Open in Outlook",
};

// ==================== COMPONENT ====================
function Meetings() {
  const navigate = useNavigate();
  const { layout } = useParams();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [view, setView] = useState("Day");
  const [currentDate, setCurrentDate] = useState(toDateObj(TODAY_STR));
  const [createOpen, setCreateOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState("mursal");
  const [toast, setToast] = useState(null);
  const createRef = useRef(null);

  // ==================== FETCH MEETINGS ====================
  useEffect(() => {
    fetchMeetings();
  }, []);

  const fetchMeetings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllMeetingsApi({ limit: 200 });
      if (response.data?.success) {
        const transformed = (response.data.data || []).map((m) => {
          // Pull the date/time numbers straight out of the response
          // string — no Date object, no UTC/local conversion. What the
          // API sent is exactly what gets shown.
          const startP = parseAsIs(m.startTime);
          const endP = parseAsIs(m.endTime);

          const startHours = String(startP.hour).padStart(2, "0");
          const startMinutes = String(startP.minute).padStart(2, "0");
          const endHours = String(endP.hour).padStart(2, "0");
          const endMinutes = String(endP.minute).padStart(2, "0");

          const year = startP.year;
          const month = String(startP.month).padStart(2, "0");
          const day = String(startP.day).padStart(2, "0");

          return {
            id: m.id,
            title: m.title,
            type: m.meetingSource === "MURSAL" ? "mursal" : "exchange",
            organizer: m.organizerEmail || "Unknown",
            date: `${year}-${month}-${day}`,
            startTime: `${startHours}:${startMinutes}`,
            endTime: `${endHours}:${endMinutes}`,
            description: m.description || "",
            invite: m.participants?.map((p) => p.email).join(", ") || "",
            reminder: "15",
            roomName: m.livekitRoomName || "",
            waitingRoomEnabled: m.waitingRoomEnabled ?? true,
            recordingEnabled: m.recordingEnabled || false,
            transcriptEnabled: m.transcriptEnabled || false,
            minutesEnabled: m.minutesEnabled || false,
            allowGuests: false,
            includeJoinLink: m.mursalLivekitEnabled || false,
            participants: m.participants || [],
            meetingStatus: m.meetingStatus,
            mursalLivekitEnabled: m.mursalLivekitEnabled,
            startTimeIso: m.startTime,
            endTimeIso: m.endTime,
            timezone: m.timezone,
          };
        });
        setMeetings(transformed);
      }
    } catch (err) {
      console.error("Failed to fetch meetings:", err);
      setError(err.response?.data?.message || "Failed to load meetings");
    } finally {
      setLoading(false);
    }
  };

  // ==================== EFFECTS ====================
  useEffect(() => {
    function handleClickOutside(e) {
      if (createRef.current && !createRef.current.contains(e.target)) {
        setCreateOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  // ==================== DATE NAVIGATION ====================
  const dateRangeLabel = useMemo(() => {
    if (view === "Day") {
      const isToday = toDateStr(currentDate) === TODAY_STR;
      return `${isToday ? "Today - " : ""}${formatLong(currentDate)}`;
    }
    if (view === "Week") {
      const start = startOfWeek(currentDate);
      const end = addDays(start, 6);
      return `${formatShort(start)} - ${formatLong(end)}`;
    }
    if (view === "Month") {
      return currentDate.toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      });
    }
    return "All Upcoming Meetings";
  }, [view, currentDate]);

  function handlePrev() {
    if (view === "Day") setCurrentDate((d) => addDays(d, -1));
    else if (view === "Week") setCurrentDate((d) => addDays(d, -7));
    else if (view === "Month") setCurrentDate((d) => addMonths(d, -1));
  }
  function handleNext() {
    if (view === "Day") setCurrentDate((d) => addDays(d, 1));
    else if (view === "Week") setCurrentDate((d) => addDays(d, 7));
    else if (view === "Month") setCurrentDate((d) => addMonths(d, 1));
  }
  function handleToday() {
    setCurrentDate(toDateObj(TODAY_STR));
  }

  // ==================== FILTERING ====================
  const filteredMeetings = useMemo(() => {
    let list = meetings.filter((m) => {
      if (activeTab === "mursal") return m.type === "mursal";
      if (activeTab === "exchange") return m.type === "exchange";
      return true;
    });

    if (view === "Day") {
      const dStr = toDateStr(currentDate);
      list = list.filter((m) => m.date === dStr);
    } else if (view === "Week") {
      const start = toDateStr(startOfWeek(currentDate));
      const end = toDateStr(addDays(startOfWeek(currentDate), 6));
      list = list.filter((m) => m.date >= start && m.date <= end);
    } else if (view === "Month") {
      const y = currentDate.getFullYear();
      const mo = currentDate.getMonth();
      list = list.filter((m) => {
        const md = toDateObj(m.date);
        return md.getFullYear() === y && md.getMonth() === mo;
      });
    } else if (view === "Schedule") {
      list = list.filter((m) => m.date >= TODAY_STR);
    }

    return [...list].sort((a, b) => {
      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
      return toMinutes(a.startTime) - toMinutes(b.startTime);
    });
  }, [meetings, activeTab, view, currentDate]);

  // ==================== DRAWER ====================
  function openDrawer(type) {
    setDrawerType(type);
    setDrawerOpen(true);
    setCreateOpen(false);
  }
  function closeDrawer() {
    setDrawerOpen(false);
  }

  // ==================== CREATE MEETING ====================

  async function handleCreateMeeting(formData) {
    try {
      // Validate title
      if (!formData.title?.trim()) {
        setToast("❌ Title is required");
        return;
      }

      let startTimeStr, endTimeStr;

      // Check if formData already has combined strings in the correct format (from MeetingsDrawer)
      if (formData.startTime && formData.startTime.includes("T")) {
        // Already in "YYYY-MM-DDTHH:MM:00.000Z" format - use as-is
        startTimeStr = formData.startTime;
        endTimeStr = formData.endTime;
      }
      // Check if formData has separate date/time fields (raw form data)
      else if (formData.date && formData.startTime && formData.endTime) {
        // Match the mobile app's `_asIs()` format exactly: literal
        // date/time with ".000Z" appended as a fixed text suffix.
        startTimeStr = `${formData.date}T${formData.startTime}:00.000Z`;
        endTimeStr = `${formData.date}T${formData.endTime}:00.000Z`;
      } else {
        console.log("📋 Form data received:", formData);
        setToast("❌ Date and time are required");
        return;
      }

      // Validate the time strings
      if (!startTimeStr || !endTimeStr) {
        setToast("❌ Invalid date/time values");
        return;
      }

      // Parse attendees - handle both formats
      let attendees = [];
      if (formData.attendees && Array.isArray(formData.attendees)) {
        attendees = formData.attendees;
      } else if (formData.invite) {
        attendees = formData.invite
          .split(",")
          .map((email) => ({ email: email.trim() }))
          .filter((a) => a.email);
      }

      const payload = {
        title: formData.title.trim(),
        organizerEmail: getUserEmail() || "admin@mursal.com",
        description: formData.description?.trim() || "",
        startTime: startTimeStr, // Sent exactly as entered, no conversion
        endTime: endTimeStr, // Sent exactly as entered, no conversion
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        attendees,
        recordingEnabled:
          formData.recordingEnabled ?? formData.recording ?? false,
        transcriptEnabled: formData.transcriptEnabled ?? false,
        minutesEnabled: formData.minutesEnabled ?? false,
        waitingRoomEnabled:
          formData.waitingRoomEnabled ?? formData.waitingRoom ?? false,
      };

      console.log("📤 Creating meeting:", payload);

      let response;
      if (formData.meetingType === "mursal" || !formData.meetingType) {
        response = await createMursalMeetingApi(payload);
      } else {
        response = await createExchangeMeetingApi({
          ...payload,
          mailbox: "default",
          createMursalLiveKitLink: formData.includeJoinLink || false,
        });
      }

      if (response.data?.success) {
        setToast("✅ Meeting created successfully!");
        closeDrawer();
        fetchMeetings();
      } else {
        setToast(
          "❌ " + (response.data?.message || "Failed to create meeting"),
        );
      }
    } catch (err) {
      console.error("Create meeting failed:", err);
      setToast(
        "❌ " +
          (err.response?.data?.message ||
            err.response?.data?.error ||
            err.message ||
            "Failed to create meeting"),
      );
    }
  }

  // ==================== JOIN MEETING ====================
  async function handleJoinMeeting(meeting) {
    try {
      setToast("Fetching join token...");
      const response = await getMeetingJoinTokenApi(meeting.id);
      if (response.data?.success) {
        navigate(`/mursal-meetings/${meeting.id}`);
      }
    } catch (err) {
      setToast("❌ Failed to get join token");
    }
  }

  function handleRowClick(meeting) {
    const status = getMeetingStatus(meeting);
    if (meeting.mursalLivekitEnabled && status === "live") {
      handleJoinMeeting(meeting);
    } else {
      navigate(`/meetings/${meeting.id}/${layout}`);
    }
  }

  // ==================== RENDER ====================
  return (
    <div className="meetings-page">
      {/* Header */}
      <div className="meetings-header">
        <div>
          <h1 className="meetings-header__title">Meetings</h1>
          <p className="meetings-header__subtitle">
            Combined calendar of Mursal meetings and Exchange meetings.
          </p>
        </div>
        <div className="meetings-header__actions" ref={createRef}>
          <button
            type="button"
            className="btn-create"
            onClick={() => setCreateOpen((o) => !o)}
          >
            <span className="btn-create__icon">+</span>
            Create Meeting
          </button>
          {createOpen && (
            <div className="create-dropdown">
              <button
                type="button"
                className="create-dropdown__item"
                onClick={() => openDrawer("mursal")}
              >
                <span className="create-dropdown__dot create-dropdown__dot--mursal" />
                <div>
                  <span className="create-dropdown__label">
                    Create Mursal Meeting
                  </span>
                  <span className="create-dropdown__hint">
                    Creates a LiveKit room instantly
                  </span>
                </div>
              </button>
              <button
                type="button"
                className="create-dropdown__item"
                onClick={() => openDrawer("exchange")}
              >
                <span className="create-dropdown__dot create-dropdown__dot--exchange" />
                <div>
                  <span className="create-dropdown__label">
                    Create Exchange Meeting
                  </span>
                  <span className="create-dropdown__hint">
                    Adds an event to Outlook calendar
                  </span>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="meetings-tabs">
        {["all", "mursal", "exchange"].map((tab) => (
          <button
            key={tab}
            type="button"
            className={`meetings-tabs__item ${activeTab === tab ? "active" : ""}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === "all"
              ? "All Meetings"
              : tab === "mursal"
                ? "Mursal Meetings"
                : "Exchange Calendar"}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="meetings-toolbar">
        <div className="view-switch">
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              className={`view-switch__btn ${view === v ? "active" : ""}`}
              onClick={() => setView(v)}
            >
              {v}
            </button>
          ))}
        </div>
        <div className="date-nav">
          {view !== "Schedule" && (
            <>
              <button
                type="button"
                className="date-nav__btn"
                onClick={handlePrev}
              >
                ‹
              </button>
              <button
                type="button"
                className="date-nav__today"
                onClick={handleToday}
              >
                Today
              </button>
              <button
                type="button"
                className="date-nav__btn"
                onClick={handleNext}
              >
                ›
              </button>
            </>
          )}
          <span className="date-nav__label">{dateRangeLabel}</span>
        </div>
      </div>

      {/* Table */}
      <div className="meetings-table-card">
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p style={{ marginTop: 12, color: "#6b7280" }}>
              Loading meetings...
            </p>
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "60px 20px" }}>
            <p style={{ color: "#ef4444", marginBottom: 12 }}>⚠️ {error}</p>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={fetchMeetings}
            >
              Retry
            </button>
          </div>
        ) : (
          <table className="meetings-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Meeting Title</th>
                <th>Type</th>
                <th>Organizer</th>
                <th>Join / Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredMeetings.length === 0 ? (
                <tr>
                  <td colSpan={5} className="meetings-table__empty">
                    No meetings found for this view.
                  </td>
                </tr>
              ) : (
                filteredMeetings.map((meeting) => {
                  const status = getMeetingStatus(meeting);
                  const isAction = status === "live" || status === "outlook";
                  return (
                    <tr
                      key={meeting.id}
                      onClick={() => handleRowClick(meeting)}
                      style={{ cursor: "pointer" }}
                    >
                      <td className="meetings-table__time">
                        <div>
                          {formatTimeRange(meeting.startTime, meeting.endTime)}
                        </div>
                        {view !== "Day" && (
                          <div className="meetings-table__date">
                            {formatShort(toDateObj(meeting.date))}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="meetings-table__title">
                          {meeting.title}
                        </div>
                        {meeting.description && (
                          <div className="meetings-table__desc">
                            {meeting.description}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`badge badge--type-${meeting.type}`}>
                          {meeting.type === "mursal" ? "Mursal" : "Exchange"}
                        </span>
                      </td>
                      <td>{meeting.organizer}</td>
                      <td>
                        {isAction ? (
                          <button
                            type="button"
                            className={`badge badge--status-${status} badge--clickable`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (status === "live") handleJoinMeeting(meeting);
                            }}
                          >
                            {STATUS_LABEL[status]}
                          </button>
                        ) : (
                          <span className={`badge badge--status-${status}`}>
                            {STATUS_LABEL[status]}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Meeting Drawer */}
      {drawerOpen && (
        <MeetingsDrawer
          meetingType={drawerType}
          currentDate={currentDate}
          onClose={closeDrawer}
          onSubmit={handleCreateMeeting}
        />
      )}

      {/* Toast */}
      {toast && <div className="meetings-toast">{toast}</div>}
    </div>
  );
}

export default Meetings;
